<template>
  <Field
    v-slot="{ errors }"
    class="veeValidate rs-multiple-inputs-wrapper"
    as="div"
    :name="label ? label : name ? name : 'field'"
    :rules="rules"
    :model-value="tagValues"
  >
    <div class="rs-multiple-inputs">
      <label v-if="label">{{ label }}</label>
      <div :class="{ 'is-invalid': !!errors[0] }">
        <input
          :id="`multiple-inputs-${id}`"
          ref="tagsInput"
          :placeholder="placeholder || $ts('multipleInputs.placeholderDefault')"
          :disabled="disabled"
        />
      </div>
    </div>
    <div
      v-if="errors.length"
      class="text--error"
    >
      {{ errors[0] }}
    </div>
  </Field>
</template>

<script lang="ts">
import { Field, defineRule } from 'vee-validate';
import { PropType, defineComponent } from 'vue';
/* eslint-disable camelcase */
import {
  alpha_num,
  digits,
  email,
  max,
  max_value,
  min,
  min_value,
  numeric,
  regex,
  required,
} from '@vee-validate/rules';
import Tagify from '@yaireo/tagify';
import { methods } from '@/mixins/validation-mixin';
import i18n from '@/i18n';

defineRule('email', (value: unknown) => (email(value) ? true : `${i18n.t('validation.emailInvalid')}`));
defineRule('required', (value: unknown) => (required(value) ? true : `${i18n.t('validation.fieldRequired')}`));
defineRule('numeric', numeric);
// Rules are registered globally, so the last module evaluated wins. This component is
// imported after RsFormInput/RsFormTextarea in the generated global component plugin,
// so registering the bare rule here would replace their localized message with
// vee-validate's untranslated default everywhere. Keep this identical to theirs.
defineRule('max', (value: unknown, params: [number]) => (max(value, params) ? true : i18n.t('validation.maxLength', { max: params[0] }) as string));
defineRule('min', min);
defineRule('max_value', max_value);
defineRule('min_value', min_value);
defineRule('digits', digits);
defineRule('regex', regex);
defineRule('url', (value: string) => (!value?.trim() || methods.isURL(value) || `${i18n.t('validation.urlInvalid')}`));
defineRule('numSpace', (value: string) => {
  const trimValue = value.replace(/\s/g, '');
  if (trimValue === '') return true;
  if (
    /^[0-9]*$/im.test(
      trimValue,
    ) === false
  ) {
    return `${i18n.t('validation.numericOnly')}`;
  }
  return true;
});
defineRule('phone', (value: string) => {
  const trimValue = value.replace(/\s/g, '');
  if (trimValue === '') return true;
  if (
    /^[+]?[0-9]?[-\s.]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/im.test(
      trimValue,
    ) === false
  ) {
    return `${i18n.t('validation.phoneInvalid')}`;
  }
  return true;
});
defineRule('alpha_num', (value: unknown) => (alpha_num(value, {}) ? true : `${i18n.t('validation.alphaNumOnly')}`));
defineRule('zipRegex', (zip: string) => {
  const zipRegex = new RegExp(
    /^\s*[0-9]{5}\s*$|^\s*[A-Z][0-9][A-Z] ?[0-9][A-Z][0-9]\s*$/,
    'i',
  );
  return zipRegex.test(String(zip).toLowerCase()) || `${i18n.t('validation.postalInvalid')}`;
});
defineRule('urlRegex', (value: string) => methods.isURL(value) || `${i18n.t('validation.urlRegexInvalid')}`);
defineRule('multiEmail', (value: string | string[]) => {
  if (value === undefined || value === null) return true;
  const commaSplit = new RegExp('\\s?[, ]\\s?');
  let isValid = true;
  const strValue = value.toString().replace(/\n/g, ',');
  let multiEmail: string[] = strValue.split(commaSplit);
  multiEmail = multiEmail.filter((e: any) => e);
  multiEmail = multiEmail.map((e: any) => e.trim());
  multiEmail.forEach((element: any) => {
    if (/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(element) === false) {
      isValid = false;
    }
  });

  return isValid || `${i18n.t('validation.emailsNotValid')}`;
});

export default defineComponent({
  name: 'RsMultipleInputs',
  components: {
    Field,
  },
  props: {
    /**
     * Validation Rules
     */
    rules: {
      type: Object,
      default: () => {},
    },
    /**
     * Set input tag id.
     */
    id: {
      type: [String, Number],
      default: '',
    },
    label: {
      type: String,
      default: '',
    },
    /**
     * Value
     */
    value: {
      type: Array as PropType<string[]|number[]>,
      default: () => [],
    },
    /**
     * Separator characters
     */
    separator: {
      type: String,
      default: '',
    },
    /**
     * Field Name
     */
    name: {
      type: String,
      default: 'input',
    },
    /**
     * Input Placeholder
     */
    placeholder: {
      type: String,
      default: null,
    },
    /**
     * Disable enter to add tag
     */
    noAddOnEnter: {
      type: Boolean,
      default: false,
    },
    /**
     * Enable backspace to delete tag
     */
    removeOnDelete: {
      type: Boolean,
      default: true,
    },
    /**
     * Disable to type
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Input tag size: sm | md | lg
     */
    size: {
      type: String,
      default: 'md',
    },
  },

  emits: ['input-change'],

  data() {
    return {
      valid: null,
      /**
       * Content-stable mirror of `value`, and what the wrapping vee-validate Field
       * binds to. See the `value` watcher for why the raw prop can't be used.
       */
      tagValues: (this.value ?? []).map((tagValue) => `${tagValue}`) as string[],
    };
  },

  watch: {
    /**
     * Most consumers pass an inline expression (`:value="x.split(',')"` and friends),
     * so a fresh array arrives on every parent render even when nothing changed.
     * Forwarding that identity straight to vee-validate's Field makes it re-validate,
     * which re-renders the parent, which allocates again — an unbounded loop that hard
     * freezes the tab (Vue 3 does not warn: the cycle spans flushes). So compare by
     * content and only propagate a genuine change.
     */
    value(newVal: string[]): void {
      const nextTags = (newVal ?? []).map((tagValue) => `${tagValue}`);
      if (this.tagValues.length === nextTags.length && this.tagValues.every((tag, index) => tag === nextTags[index])) {
        return;
      }
      this.tagValues = nextTags;

      const instance = (this as any).tagifyInstance as Tagify | undefined;
      if (!instance) return;
      (this as any)._suppressSync = true;
      instance.removeAllTags({ withoutChangeEvent: true } as any);
      if (nextTags.length) instance.addTags(nextTags);
      (this as any)._suppressSync = false;
    },
    disabled(val: boolean): void {
      // setDisabled(), not setReadonly(): tagify seeds `settings.disabled` from the source
      // input's attribute, and its event binding bails while that flag is set. setReadonly()
      // only ever touches `settings.readonly`, so re-enabling left the control inert.
      (this as any).tagifyInstance?.setDisabled(val);
    },
  },

  mounted() {
    // 'tab' commits the pending text and keeps focus in the control, ready for the next tag —
    // tagify routes Tab through its Enter branch, which preventDefault()s the focus move. That
    // matches the baseline BFormTags control, whose Tab landed on its own per-row "Add" button
    // rather than leaving the field. With an empty input tagify returns before preventDefault, so
    // Tab still tabs out and the field is never a keyboard trap.
    //
    // 'blur' commits whatever is still typed when focus leaves the field, so a value the user never
    // confirmed with a key isn't silently dropped (and the form registers the change).
    const addTagOn = this.noAddOnEnter ? ['blur'] : ['blur', 'enter', 'tab'];
    const delimiters = this.separator
      ? this.separator.split('').map((char) => char.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')
      : ',';
    const instance = new Tagify(this.$refs.tagsInput as HTMLInputElement, {
      delimiters,
      // Boolean, never 'edit': tagify's three-state setting maps `true` to removeTags(tagBefore)
      // and 'edit' to editTag(tagBefore), which reopens the previous tag for character-by-character
      // editing instead of deleting it. Baseline (BFormTags `remove-on-delete`) removed the whole tag.
      backspace: this.removeOnDelete,
      addTagOn,
      // Keep the native input's value a plain comma-joined list (tagify's
      // default serializes to JSON, which leaks into validators/serializers
      // that read the DOM value).
      originalInputValueFormat: (values: { value: string }[]) => values.map((t) => t.value).join(','),
      // Required so origSettings.dropdown is defined; setReadonly() restores
      // dropdown.enabled from origSettings and crashes if it was never set.
      dropdown: { enabled: false },
    } as any);
    (this as any).tagifyInstance = instance;

    if (this.value?.length) {
      instance.addTags(this.value as string[]);
    }
    instance.on('change', () => {
      if ((this as any)._suppressSync) return;
      const tags = (instance.value ?? []).map((t: any) => t.value);
      this.$emit('input-change', tags);
    });
    if (this.disabled) {
      instance.setDisabled(true);
    }
  },

  beforeUnmount() {
    (this as any).tagifyInstance?.destroy();
  },
});
</script>

<style lang="scss" scoped>
  @import '@/styles/_variables.scss';
  .rs-multiple-inputs-wrapper {
    margin-bottom: 1rem;
  }

  .rs-multiple-inputs {

    :deep(.tagify) {
      --tag-bg: #{$secondary-color};
      // Tracks --tag-bg so hover/focus is a no-op: tagify otherwise tints the whole pill, which
      // flickers as the cursor crosses a row of tags. Baseline's BFormTags badges had no hover
      // state. Same treatment as the tagify-tag-parity mixin applies to the other two wrappers.
      --tag-hover: var(--tag-bg);
      // Hovering the x also repaints the pill (`.tagify__tag__removeBtn:hover + div::before`
      // sets box-shadow: --tag-remove-bg inset !important). Default is a pinkish red that
      // fights the secondary background; track --tag-bg so the shadow is invisible.
      --tag-remove-bg: var(--tag-bg);
      --tag-text-color: #{$white};
      --tag-border-color: transparent;
      --tag-remove-btn-color: rgba(255, 255, 255, 0.7);
      --tag-remove-btn-bg--hover: rgba(0, 0, 0, 0.15);
      --tag-pad: 0.1em 0.35em;
      --placeholder-color: #6c757d;

      border: 1px solid #ced4da;
      border-radius: 0.25rem;
      padding: 0.15rem 0.5rem;
      min-height: 0;
      height: auto;
      width: 100%;
      transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
    }
    :deep(.tagify__input) {
      min-width: 8em; // always show the placeholder even when tags are present
      margin: 2px 0;
      align-self: center;
    }
    :deep(.tagify__input::before) {
      font-weight: 300;
    }
    :deep(.tagify__tag) {
      margin-block: 2px;
    }
    // The tint above is only half of it: tagify also pulls the pill background's inset outward
    // on hover/focus, so the pill visibly grows. Pin it to the resting value.
    :deep(.tagify__tag:hover:not([readonly]) > div::before),
    :deep(.tagify__tag:focus > div::before) {
      --tag-bg-inset: 0;
    }
    // Tagify also drops the label to opacity .5 while the x is hovered, which reads as the tag
    // fading out just as the user is trying to click. Keep the text at full opacity.
    :deep(.tagify__tag__removeBtn:hover + div > span) {
      opacity: 1;
    }
    :deep(.tagify:focus-within) {
      border-color: #80bdff;
      box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
      outline: none;
    }
    :deep(.tagify--disabled) {
      background-color: #e9ecef;
      pointer-events: none;
    }
    :deep(.tagify__tag > div) {
      border-radius: 0.2em;
      font-size: 0.78rem;
    }
    :deep(.tagify__tag-text) {
      font-size: 0.78rem;
    }
    :deep(.is-invalid .tagify) { border-color: #dc3545; }
    :deep(.is-invalid .tagify:focus-within) { box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25); }
  }
</style>
